import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import {
  HistoryRecord,
  AgeGroup,
  HistoryFilter,
  HistorySortField,
  HistorySortDirection
} from '../../types/domain';
import {
  getInsulinTypeOptions,
  getDateTimeValue,
  getInsulinTypeLabel,
  getSiteLabelForHistory,
  getAgeGroupLabel,
  formatDateTime
} from '../../lib/domain-data';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { cn } from '../../lib/utils';

export default function HistoryListPage() {
  const { user } = useAuth();
  const [records, setRecords] = useState<HistoryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<HistoryFilter>({
    ageGroup: 'all',
    insulinTypeId: 'all'
  });
  const [sortField, setSortField] = useState<HistorySortField>('datetime');
  const [sortDirection, setSortDirection] = useState<HistorySortDirection>('desc');
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 10;
  const [selectedRecord, setSelectedRecord] = useState<HistoryRecord | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadHistory = async () => {
      setIsLoading(true);
      setError(null);

      if (!user) {
        if (!cancelled) {
          setIsLoading(false);
        }
        return;
      }

      try {
        const history = await dataService.getHistory(user.id);

        if (!cancelled) {
          setRecords(history);
          setSortField('datetime');
          setPageIndex(0);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError('Terjadi kesalahan saat memuat riwayat suntikan.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleFilterAgeGroupChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as AgeGroup | 'all';
    setFilter(current => ({
      ...current,
      ageGroup: value
    }));
    setPageIndex(0);
  };

  const handleFilterInsulinTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as string | 'all';
    setFilter(current => ({
      ...current,
      insulinTypeId: value
    }));
    setPageIndex(0);
  };

  const handleToggleSortDirection = () => {
    setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
    setPageIndex(0);
  };

  const handleSelectRecord = (record: HistoryRecord) => {
    setSelectedRecord(record);
  };

  const handleCloseDetail = () => {
    setSelectedRecord(null);
  };

  const handlePrevPage = () => {
    setPageIndex(current => Math.max(0, current - 1));
  };

  const handleNextPage = (pageCount: number) => {
    setPageIndex(current => Math.min(pageCount - 1, current + 1));
  };

  const insulinOptions = getInsulinTypeOptions();

  const filteredAndSorted = useMemo(() => {
    const filtered = records.filter(record => {
      if (filter.ageGroup !== 'all' && record.ageGroup !== filter.ageGroup) {
        return false;
      }
      if (
        filter.insulinTypeId !== 'all' &&
        record.insulinTypeId !== filter.insulinTypeId
      ) {
        return false;
      }
      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortField === 'datetime') {
        const aValue = getDateTimeValue(a.date, a.time);
        const bValue = getDateTimeValue(b.date, b.time);
        if (aValue === bValue) {
          return 0;
        }
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (sortField === 'ageGroup') {
        const order: Record<AgeGroup, number> = {
          '2-10': 0,
          '11-plus': 1
        };
        const aValue = order[a.ageGroup];
        const bValue = order[b.ageGroup];
        if (aValue === bValue) {
          return 0;
        }
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (sortField === 'siteId') {
        const aVal = a.siteId ?? -1;
        const bVal = b.siteId ?? -1;
        if (aVal === bVal) {
          return 0;
        }
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aLabel = getInsulinTypeLabel(a.insulinTypeId);
      const bLabel = getInsulinTypeLabel(b.insulinTypeId);
      if (aLabel === bLabel) {
        return 0;
      }
      if (sortDirection === 'asc') {
        return aLabel.localeCompare(bLabel);
      }
      return bLabel.localeCompare(aLabel);
    });

    return sorted;
  }, [records, filter, sortField, sortDirection]);

  const pageCount = useMemo(() => {
    if (!filteredAndSorted.length) {
      return 1;
    }
    return Math.max(1, Math.ceil(filteredAndSorted.length / pageSize));
  }, [filteredAndSorted.length, pageSize]);

  const currentPageIndex = Math.min(pageIndex, pageCount - 1);

  const pagedRecords = useMemo(() => {
    const start = currentPageIndex * pageSize;
    const end = start + pageSize;
    return filteredAndSorted.slice(start, end);
  }, [filteredAndSorted, currentPageIndex, pageSize]);

  const totalCount = filteredAndSorted.length;
  const rangeStart = totalCount === 0 ? 0 : currentPageIndex * pageSize + 1;
  const rangeEnd = Math.min(totalCount, (currentPageIndex + 1) * pageSize);

  return (
    <div className="screen">
      <div
        className="screen-card"
        role="group"
        aria-labelledby="history-title"
      >
        <div id="history-title" className="section-title">
          Riwayat suntikan
        </div>
        <div className="subtitle">
          Lihat daftar suntikan sebelumnya berdasarkan usia dan jenis insulin.
        </div>

        <div className="field-group">
          <div className="field">
            <label htmlFor="history-age-filter">Filter usia</label>
            <select
              id="history-age-filter"
              value={filter.ageGroup}
              onChange={handleFilterAgeGroupChange}
            >
              <option value="all">Semua</option>
              <option value="2-10">2–10 tahun</option>
              <option value="11-plus">≥11 tahun</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="history-insulin-filter">Filter jenis insulin</label>
            <select
              id="history-insulin-filter"
              value={filter.insulinTypeId}
              onChange={handleFilterInsulinTypeChange}
            >
              <option value="all">Semua</option>
              {insulinOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field-group" style={{ marginTop: 8 }}>
          <div className="field">
            <label>Urutkan</label>
            <button
              type="button"
              className="btn-secondary w-full md:w-auto"
              onClick={handleToggleSortDirection}
            >
              {sortDirection === 'asc' ? 'Terlama ke terbaru' : 'Terbaru ke terlama'}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="subtitle" style={{ marginTop: 16 }}>
            Memuat riwayat suntikan...
          </div>
        ) : null}

        {error ? (
          <div className="subtitle" style={{ marginTop: 16, color: '#b91c1c' }}>
            {error}
          </div>
        ) : null}

        {!isLoading && !totalCount ? (
          <div className="subtitle" style={{ marginTop: 16 }}>
            Belum ada riwayat suntikan yang tersimpan.
          </div>
        ) : null}

        {!isLoading && totalCount > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {pagedRecords.map((record, index) => {
              const siteLabel = getSiteLabelForHistory(
                record.ageGroup,
                record.siteId
              );
              const insulinLabel = getInsulinTypeLabel(record.insulinTypeId);
              const key = `${record.ageGroup}-${record.date}-${record.time}-${record.siteId}-${index}`;

              return (
                <Card
                  key={key}
                  className="cursor-pointer hover:bg-slate-50 transition-colors border-slate-200 shadow-sm hover:shadow-md"
                  onClick={() => handleSelectRecord(record)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-slate-500">{record.date}</span>
                        <span className="text-2xl font-bold text-slate-700">{record.time}</span>
                      </div>
                      <Badge variant="outline" className={cn(
                        "ml-2 whitespace-nowrap",
                        record.ageGroup === '2-10' ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-purple-50 text-purple-700 border-purple-200"
                      )}>
                        {getAgeGroupLabel(record.ageGroup)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex flex-col">
                        <span className="text-xs uppercase text-slate-400 font-bold tracking-wider mb-0.5">
                          {record.siteId === null ? 'Tindakan' : 'Lokasi'}
                        </span>
                        <span className="text-sm font-semibold text-slate-700 leading-tight">{siteLabel}</span>
                      </div>
                      {record.siteId !== null && (
                        <div className="flex flex-col">
                          <span className="text-xs uppercase text-slate-400 font-bold tracking-wider mb-0.5">Insulin</span>
                          <span className="text-sm font-medium text-slate-700">
                            {insulinLabel}
                            {record.insulinBrand && ` (${record.insulinBrand})`}
                          </span>
                        </div>
                      )}
                      {record.glucoseValue !== undefined && record.glucoseValue !== null && (
                        <div className="flex flex-col">
                          <span className="text-xs uppercase text-slate-400 font-bold tracking-wider mb-0.5">Gula Darah</span>
                          <span className="text-sm font-medium text-slate-700">
                            {record.glucoseValue} <span className="text-xs opacity-70">mg/dL</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : null}

        {totalCount > 0 ? (
          <div className="next-actions" style={{ marginTop: 16 }}>
            <div>
              Menampilkan {rangeStart}–{rangeEnd} dari {totalCount} catatan
            </div>
            <div className='flex gap-2'>
              <button
                type="button"
                className="btn-secondary w-full md:w-auto"
                onClick={handlePrevPage}
                disabled={currentPageIndex === 0}
                aria-disabled={currentPageIndex === 0}
              >
                Sebelumnya
              </button>
              <button
                type="button"
                className="btn-primary w-full md:w-auto"
                onClick={() => handleNextPage(pageCount)}
                disabled={currentPageIndex >= pageCount - 1}
                aria-disabled={currentPageIndex >= pageCount - 1}
                style={{ marginLeft: 8 }}
              >
                Berikutnya
              </button>
            </div>
          </div>
        ) : null}

        <div className="subtitle" style={{ marginTop: 16 }}>
          <Link to="/">Kembali ke beranda</Link>
        </div>
      </div>

      {selectedRecord ? (
        <div
          className="screen-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="history-detail-title"
          style={{ marginTop: 16 }}
        >
          <div id="history-detail-title" className="section-title">
            Detail suntikan
          </div>
          <div className="subtitle">
            Data diambil dari langkah Suntik insulin, termasuk lokasi dan jenis suntikan.
          </div>

          <div className="field-group" style={{ marginTop: 12 }}>
            <div className="field">
              <label>Tanggal dan jam</label>
              <div>{formatDateTime(selectedRecord.date, selectedRecord.time)}</div>
            </div>
            <div className="field">
              <label>Usia</label>
              <div>{getAgeGroupLabel(selectedRecord.ageGroup)}</div>
            </div>
          </div>

          <div className="field-group" style={{ marginTop: 12 }}>
            <div className="field">
              <label>{selectedRecord.siteId === null ? 'Tindakan' : 'Lokasi suntikan'}</label>
              <div>
                {getSiteLabelForHistory(selectedRecord.ageGroup, selectedRecord.siteId)}
              </div>
            </div>
            {selectedRecord.siteId !== null && (
              <div className="field">
                <label>Jenis insulin</label>
                <div>{getInsulinTypeLabel(selectedRecord.insulinTypeId)}</div>
              </div>
            )}
          </div>

          <div className="field-group" style={{ marginTop: 12 }}>
            {selectedRecord.glucoseValue !== undefined && selectedRecord.glucoseValue !== null && (
              <div className="field">
                <label>Gula Darah</label>
                <div>{selectedRecord.glucoseValue} mg/dL</div>
              </div>
            )}
            {selectedRecord.insulinBrand && (
              <div className="field">
                <label>Merk Insulin</label>
                <div>{selectedRecord.insulinBrand}</div>
              </div>
            )}
            {selectedRecord.insulinDose !== undefined && selectedRecord.insulinDose !== null && (
              <div className="field">
                <label>Dosis</label>
                <div>{selectedRecord.insulinDose} unit</div>
              </div>
            )}
          </div>

          <div className="next-actions" style={{ marginTop: 16 }}>
            <button
              type="button"
              className="btn-primary w-full md:w-auto"
              onClick={handleCloseDetail}
            >
              Tutup
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
